import { Track } from "@/src/lib/tracking";
import { useCallback, useState } from "react";
import { OspToast } from "@ospweb/sdk-ui";
import { pubsub } from "utils";

import { ROUTER_PATH } from "@config/router";
import {
  getPaymentCheckErrorMessage,
  isCrossChannelBlocked,
} from "@hooks/useAppPurchaseFlow";
import { usePayment } from "@hooks/usePayment";
import { sendBeginCheckoutGtm } from "@utils/member/purchaseGtm";
import { MemberProduct, PayChannel } from "@features/api/memberApi";
import { pubSubKeyMap } from "@utils/pubSubKeyMap";
import { openCrossChannelGuideModal } from "./CrossChannelGuideModal/CrossChannelGuideModalCreateAble";

interface UseMemberCheckoutParams {
  data?: MemberProduct[];
}

/**
 * 定价页 web 下单（PC 定价卡 / H5 footer 共用，EDG-552 起渠道无关）。
 *
 * 渠道由调用方从 `pickCheckoutChannels` 取得（主按钮 = primary，次级 = secondary[i]），
 * 这里不做任何渠道判断 —— 渠道优先级只来自后端 `payment_configs` 顺序（D-11）。
 */
export const useMemberCheckout = ({ data }: UseMemberCheckoutParams) => {
  const { handlePaymentCheck } = usePayment();
  const [isPaying, setIsPaying] = useState(false);

  const handleCheckout = useCallback(
    async (planId: string, payChannel: PayChannel) => {
      setIsPaying(true);
      // data 含试用产品（useMemberFeature 的 select 只派生 trialList，不过滤），试用 SKU 也能命中
      const currentPlan = data?.find((item) => item.id === planId);

      const { data: checkoutUrl, error } = await handlePaymentCheck(planId, {
        payChannel,
        product: currentPlan,
      });
      Track.log("info", "Member", `[trace][pay] ${payChannel} result`, {
        arg1: String(checkoutUrl),
        arg2: String(error),
      });
      setIsPaying(false);

      if (isCrossChannelBlocked(error)) {
        // 其他渠道有生效中的订阅：弹引导而不是只给一句提示。只引导、不代关续费；
        // PC 由 ManageBillingUpgrade 打开账单管理弹窗，H5 的跳转在弹窗的链接里完成
        const guideResult = await openCrossChannelGuideModal();
        if (guideResult === "manage") {
          pubsub.emit(pubSubKeyMap.MEMBER_OPEN_MANAGE_BILLING);
        }
        return;
      }
      if (error) {
        OspToast.error({
          content: getPaymentCheckErrorMessage({
            code: error.code,
            msg: error.msg,
          }),
        });
        return;
      }
      if (!checkoutUrl) {
        // API 成功但无支付链接（如 PayPal 下单失败返回 eligible=false）：
        // 不能裸跳转刷新页面，先提示失败（begin_checkout 也尚未上报）
        Track.log("error", "Member", "[trace][pay] empty checkout link", {
          arg1: planId,
          arg2: payChannel,
        });
        OspToast.error({
          content: getPaymentCheckErrorMessage({}),
        });
        return;
      }

      // item_id 用入参 planId 而非 currentPlan.id：产品列表没命中时 currentPlan 是 undefined，
      // 但我们仍然知道用户结的是哪个套餐，不能让这条事件退化成没有商品 id。
      sendBeginCheckoutGtm(
        { id: planId, name: currentPlan?.name, price: currentPlan?.price },
        { payChannel },
      );
      // 跳转前清理当前 history 条目，支付页点浏览器返回时会回到无参数的 /member
      window.history.replaceState({}, "", ROUTER_PATH.MEMBER);
      window.location.href = checkoutUrl;
    },
    [data, handlePaymentCheck],
  );

  return {
    isPaying,
    handleCheckout,
  };
};
